import React, { useEffect } from "react";
import { siteMap } from "internal-sitemap";
import { Redirect } from "react-router";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { RouteDefinition } from "utilities/interfaces/route-definition";
import { RouteUtils } from "utilities/route-utils";
import { UserSettingsKeys } from "constants/user-settings-keys";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import {
    NestedRoutes,
    NestedRoutesProps,
} from "utilities/routing/nested-route";
import NotebookSidebar from "pages/notebooks/sidebar/notebook-sidebar";
import BreadcrumbsMenu from "organisms/breadcrumbs/breadcrumbs-menu";
import useToggleNotebookSidebar from "pages/notebooks/use-toggle-notebook-sidebar";
import NotebookReferencePanel from "pages/notebooks/notebook-reference-panel/notebook-reference-panel";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ButtonSizes } from "atoms/constants/button-sizes";
import Icon from "atoms/icons/icon";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import useToggleNotebookReferencePanel from "pages/notebooks/notebook-reference-panel/use-toggle-notebook-reference-panel";
import useToggleNotebookChatPanel from "pages/notebooks/notebook-chat-panel/use-toggle-notebook-chat-panel";
import NotebookChatPanel from "pages/notebooks/notebook-chat-panel/notebook-chat-panel";
import useAiChatEnabled from "utilities/hooks/use-ai-chat-enabled";
import { SharedDragAndDropContextProvider } from "utilities/contexts/shared-drag-and-drop/shared-drag-and-drop-context-provider";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import useNotebooksRedirects from "utilities/hooks/domain/notebooks/use-notebooks-redirects";
import { SkipNavContent } from "@reach/skip-nav";

const BASE_CLASS = "c-notebooks-layout";

const NotebooksLayout: React.FC<any> = (props: NestedRoutesProps) => {
    const { useNotebooks: notebooksFeatureFlag, useCasiInNotebooks } =
        useFeatureFlags();
    const { globalState } = useGlobalState();
    const { refresh: getAllNotebooks } = useNotebooksAtom();
    const { isLaptop, isMobile } = useNotebookBreakpoints();
    const { showSidebar, toggleDisplay: toggleNotebookSidebar } =
        useToggleNotebookSidebar();
    const {
        toggleDisplay: toggleReferencePanel,
        showPanel,
        closePanel: closeReferencePanel,
    } = useToggleNotebookReferencePanel();
    const {
        toggleDisplay: toggleChatPanel,
        showPanel: showChatPanel,
        closePanel: closeChatPanel,
    } = useToggleNotebookChatPanel();
    const { enableAIChat } = useAiChatEnabled();
    const { syncLocalStorageWithCurrentNotebookId } = useNotebooksRedirects();

    const shouldShowSidebar = showSidebar ? "-show" : "-collapse";
    const enableNotebookSetting =
        globalState.getUserSettingValue(UserSettingsKeys.EnableNotebook) ??
        false;

    const showCasi = enableAIChat && useCasiInNotebooks && !isMobile;

    const handleReferencePanelClick = () => {
        if (showChatPanel) {
            closeChatPanel();
        }

        if (showSidebar && isLaptop) {
            toggleNotebookSidebar();
        }

        toggleReferencePanel();
    };

    const handleChatPanelClick = () => {
        if (showPanel) {
            closeReferencePanel();
        }

        toggleChatPanel();
    };

    // Fetch notebooks at the layout level.
    useEffect(() => {
        getAllNotebooks();
    }, [getAllNotebooks]);

    useEffect(() => {
        syncLocalStorageWithCurrentNotebookId();
    }, [syncLocalStorageWithCurrentNotebookId]);

    // Redirect if flag off or user is not authorized
    if (!notebooksFeatureFlag || !enableNotebookSetting) {
        return <Redirect to={siteMap.errors.notFound} />;
    }

    return (
        <>
            <BreadcrumbsMenu fullWidth={true} />
            <div className={BASE_CLASS} data-test-id="notebooks--layout">
                <SharedDragAndDropContextProvider>
                    <NotebookSidebar />
                    <div
                        className={`${BASE_CLASS}__container ${shouldShowSidebar}`}>
                        <Button
                            cssClassName={`${BASE_CLASS}__referencePanelToggleButton ${showPanel ? "-active" : ""}`}
                            onClick={handleReferencePanelClick}
                            size={ButtonSizes.XSmall}
                            style={ButtonStyles.None}
                            dataTestId="notebooks--reference-panel-toggle-button">
                            <Icon
                                cssClassName={`${BASE_CLASS}__referencePanelToggleIcon`}
                                size={IconSizes.Large}
                                type={Icons.ReferencePanel}
                            />
                        </Button>
                        {showCasi && (
                            <Button
                                cssClassName={`${BASE_CLASS}__chatPanelToggleButton ${showChatPanel ? "-active" : ""}`}
                                onClick={handleChatPanelClick}
                                size={ButtonSizes.XSmall}
                                style={ButtonStyles.None}
                                dataTestId="notebooks--chat-panel-toggle-button">
                                <Icon
                                    cssClassName={`${BASE_CLASS}__chatPanelToggleIcon`}
                                    size={IconSizes.Large}
                                    type={Icons.Casi}
                                />
                            </Button>
                        )}
                        <SkipNavContent tabIndex={-1}>
                            <NestedRoutes
                                routes={props.routes.map(
                                    (route: RouteDefinition) =>
                                        RouteUtils.withCulture<RouteDefinition>(
                                            route
                                        )
                                )}
                            />
                        </SkipNavContent>
                    </div>
                    <NotebookReferencePanel />
                    {showCasi && <NotebookChatPanel />}
                </SharedDragAndDropContextProvider>
            </div>
        </>
    );
};

export default NotebooksLayout;
